import { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Checkbox, FormButtonGroup, Input } from 'components/forms'
import { Col, Collapse, Row } from 'react-bootstrap'
import { track } from 'lib/analytics'
import Link from 'components/link'

import {
  getCollaborators,
  getPartnerCollaborator,
  getUserEmail,
} from 'registry/reducers'
import css from './partner-access-form.scss'
import * as api from 'registry/api'
import { parseErrors } from 'lib/api-utils'
import { SET_REGISTRY } from 'registry/actions'
import { SYSTEM_ERROR_KEY, SYSTEM_ERROR_MESSAGE } from 'shared/constants'
// import { email as validateEmail, required as validatePresence } from 'lib/form-validation'
import {
  createValidator,
  email,
  MSG_EMAIL_REQUIRED,
  required,
  notEquals,
} from 'lib/form-validation'

export const MSG_PARTNER_EMAIL_NOT_UNIQUE =
  "Whoops! You can't add yourself as your partner!"
export const STATE_DISALLOWED = 'disallowed'
export const STATE_INVITE = 'invite'
export const STATE_INVITED = 'invited'
export const STATE_CONFIRM_REVOKE = 'confirmRevoke'

export class PartnerAccessForm extends Component {
  constructor(props) {
    super(props)
    const { partnerCollaborator: partner } = this.props
    this.state = {
      invitationState: partner ? STATE_INVITED : STATE_DISALLOWED,
      email: partner ? partner.email : null,
      justInvited: false,
      submitting: false,
      errors: {},
    }
  }

  static propTypes = {
    collaborators: PropTypes.array,
    registryId: PropTypes.number.isRequired,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    partnerCollaborator: PropTypes.object,
    onSubmit: PropTypes.func,
    userEmail: PropTypes.string,
  }

  fieldValidator = () => {
    return createValidator({
      email: [
        {
          rule: required,
          msg: MSG_EMAIL_REQUIRED,
        },
        email,
        {
          rule: (value) => {
            if (notEquals(this.props.userEmail, value)) {
              return MSG_PARTNER_EMAIL_NOT_UNIQUE
            }
          },
        },
      ],
    })
  }

  render() {
    const { invitationState } = this.state
    const { partnerCollaborator: partner } = this.props
    let inviteButton

    switch (invitationState) {
      case STATE_INVITE:
        inviteButton = (
          <Link
            className="input-group-addon--submit"
            onClick={this.handleClickInvite.bind(this)}
          >
            {this.state.submitting ? 'Inviting...' : 'Invite'}
          </Link>
        )
        break
      case STATE_INVITED:
        inviteButton = (
          <Link
            className="input-group-addon--danger"
            onClick={this.handleClickRevoke.bind(this)}
          >
            Remove
          </Link>
        )
        break
    }

    return (
      <fieldset>
        {this.state.errors.name ? (
          <p className="text-danger h6" style={{ marginTop: -15 }}>
            {this.state.errors.name}
          </p>
        ) : null}
        <Collapse
          in={
            invitationState === STATE_DISALLOWED ||
            invitationState === STATE_INVITE
          }
        >
          <div>
            <Checkbox
              groupClassName={css.partnerAccessCheckbox}
              checked={invitationState !== STATE_DISALLOWED}
              onChange={this.handleAllowPartnerAccessChange.bind(this)}
              label="Invite my partner to edit our registry"
            />
          </div>
        </Collapse>
        <Collapse in={invitationState !== STATE_DISALLOWED}>
          <Row>
            <Col sm={6}>
              <Input
                addonAfter={inviteButton}
                id="partner_email"
                label="Partner's Email Address"
                value={this.state.email}
                error={this.state.errors.email}
                disabled={
                  invitationState !== STATE_INVITE &&
                  invitationState !== STATE_DISALLOWED
                }
                onKeyDown={this.handleKeyDown.bind(this)}
                onChange={this.handlePartnerEmailChange.bind(this)}
              />
            </Col>

            <Col xs={12}>
              {invitationState === STATE_CONFIRM_REVOKE ? (
                <div className={css.revokeConfirmationContainer}>
                  <div className="h5 mtn mbs">
                    <b>Remove {partner.firstName || partner.email}'s access?</b>
                  </div>
                  <p className="mbm">
                    Are you sure you want to revoke{' '}
                    {partner.firstName || 'your partner'}'s access to your baby
                    registry? {partner.firstName || 'Your partner'} will no
                    longer be able to add or remove items on your baby registry.
                  </p>
                  <FormButtonGroup
                    onClickPrimary={this.handleClickConfirmRevoke.bind(this)}
                    onClickSecondary={this.handleClickCancelRevoke.bind(this)}
                    submitting={this.state.submitting}
                    textPrimary={
                      this.state.submitting ? 'Removing...' : 'Remove'
                    }
                    textSecondary="Cancel"
                    submit={false}
                  />
                </div>
              ) : null}
            </Col>
          </Row>
        </Collapse>
      </fieldset>
    )
  }

  handleAllowPartnerAccessChange() {
    const nextState =
      this.state.invitationState === STATE_DISALLOWED
        ? STATE_INVITE
        : STATE_DISALLOWED
    this.setState({ invitationState: nextState })
  }

  handlePartnerEmailChange(e) {
    this.setState({ email: e.target.value })
  }

  handleClickInvite() {
    this.setState(
      { errors: this.fieldValidator()({ email: this.state.email }) },
      () => {
        if (Object.keys(this.state.errors).length) return

        const { collaborators } = this.props

        const partner = {
          email: this.state.email,
          firstName: this.props.firstName,
          lastName: this.props.lastName,
          role: 'partner',
        }

        this.saveCollaborators([...collaborators, partner]).then((data) => {
          this.setState({ justInvited: true })
          track.collaboratorInvited({
            collaboratorRole: track.CollaboratorRole.PARTNER,
          })
        })
      }
    )
  }

  handleClickRevoke() {
    this.setState({ invitationState: STATE_CONFIRM_REVOKE })
  }

  handleClickCancelRevoke() {
    this.setState({ invitationState: STATE_INVITED })
  }

  handleClickConfirmRevoke() {
    const { collaborators, partnerCollaborator: partner } = this.props
    this.saveCollaborators(
      collaborators.filter((c) => c.userId !== partner.userId)
    ).then((data) => {
      this.setState({ email: null })
    })
  }

  handleKeyDown(e) {
    // submit with return key
    if (e.keyCode === 13) {
      e.preventDefault()
      this.handleClickInvite()
    }
  }

  saveCollaborators(collaborators) {
    this.setState({ submitting: true })
    const registryData = {
      registry: {
        id: this.props.registryId,
        userEmail: this.props.userEmail,
        collaborators: collaborators,
      },
    }
    return api
      .saveRegistry(registryData)
      .then((resp) => {
        let nextInvitationState
        let flashMsg
        if (this.state.invitationState === STATE_INVITE) {
          nextInvitationState = STATE_INVITED
          flashMsg = `We've sent an invite to ${
            this.props.firstName || this.state.email
          }!`
        } else {
          nextInvitationState = STATE_DISALLOWED
          flashMsg = `${
            this.props.firstName || this.state.email || 'Your partner'
          } no longer has access to your registry.`
        }
        PubSub.publish(BLConstants.FLASH_MESSAGE, {
          message: flashMsg,
          variant: 'success',
        })
        this.setState(
          { submitting: false, invitationState: nextInvitationState },
          () => {
            this.props.dispatch({ type: SET_REGISTRY, registry: resp.registry })
            if (this.props.onSubmit) {
              this.props.onSubmit()
            }
          }
        )
      })
      .catch((resp) => {
        this.setState({ submitting: false })
        if (resp[SYSTEM_ERROR_KEY]) {
          PubSub.publish(BLConstants.FLASH_MESSAGE, {
            message: resp[SYSTEM_ERROR_KEY],
            variant: 'danger',
          })
        } else {
          const errors = parseErrors(resp)
          try {
            // TODO: might not always be collaborators[0]...
            const collaboratorErrors = errors.registry.collaborators[0]
            if (collaboratorErrors) {
              if (collaboratorErrors.firstName || collaboratorErrors.lastName) {
                this.setState({
                  errors: {
                    name: "Please add your partner's first and last name",
                  },
                })
              }
              if (collaboratorErrors.email) {
                this.setState({
                  errors: { email: `Email ${collaboratorErrors.email[0]}` },
                })
              }
            }
          } catch (e) {
            PubSub.publish(BLConstants.FLASH_MESSAGE, {
              message: SYSTEM_ERROR_MESSAGE,
              variant: 'danger',
            })
          }
        }
      })
  }
}

const mapStateToProps = (state) => {
  return {
    collaborators: getCollaborators(state),
    partnerCollaborator: getPartnerCollaborator(state),
    userEmail: getUserEmail(state),
  }
}

export default connect(mapStateToProps)(PartnerAccessForm)
